import { SocialMediaPostData } from "@contracts/shareStop";
import { SocialChannels } from "@contracts/socialNetworks";

export type ChannelEditTarget = "template" | SocialChannels;

export function shouldUseChannelOverrides(post: SocialMediaPostData | null | undefined): boolean {
  if (!post) return false;
  if (typeof post.useChannelOverrides === "boolean") return post.useChannelOverrides;
  return false;
}

export function areAssetListsEqual(first: Array<string> = [], second: Array<string> = []): boolean {
  if (first.length !== second.length) return false;
  for (let i = 0; i < first.length; i++) {
    if (first[i] !== second[i]) return false;
  }
  return true;
}

export function getChannelOverride(post: SocialMediaPostData | null | undefined, channel: SocialChannels) {
  return post?.channelOverrides?.find(overrideItem => overrideItem.socialChannel === channel) ?? null;
}

export function ensureChannelOverride(post: SocialMediaPostData | null | undefined, channel: SocialChannels) {
  if (!post) return null;
  if (!post.channelOverrides) post.channelOverrides = [];
  const existing = post.channelOverrides.find(overrideItem => overrideItem.socialChannel === channel);
  if (existing) return existing;
  const created = {
    socialChannel: channel,
    caption: post.caption ?? "",
    assetIds: [...(post.assetIds ?? [])],
  };
  post.channelOverrides.push(created);
  return created;
}

export function cleanupChannelOverrideIfTemplateMatch(post: SocialMediaPostData | null | undefined, channel: SocialChannels) {
  if (!post?.channelOverrides?.length) return;
  const overrideItem = getChannelOverride(post, channel);
  if (!overrideItem) return;
  const templateCaption = post.caption ?? "";
  const overrideCaption = (overrideItem.caption ?? "").trim() ? overrideItem.caption ?? "" : templateCaption;
  const templateAssets = post.assetIds ?? [];
  const overrideAssets = (overrideItem.assetIds?.length ?? 0) > 0 ? overrideItem.assetIds ?? [] : templateAssets;
  const isSameAsTemplate = overrideCaption === templateCaption && areAssetListsEqual(overrideAssets, templateAssets);
  if (!isSameAsTemplate) return;
  post.channelOverrides = post.channelOverrides.filter(item => item.socialChannel !== channel);
  if (!post.channelOverrides.length) post.channelOverrides = null;
}

export function getCaptionForTarget(post: SocialMediaPostData | null | undefined, target: ChannelEditTarget): string {
  if (!post) return "";
  if (target === "template") return post.caption ?? "";
  if (!shouldUseChannelOverrides(post)) return post.caption ?? "";
  const overrideCaption = getChannelOverride(post, target)?.caption;
  if (!overrideCaption?.trim()) return post.caption ?? "";
  return overrideCaption;
}

export function setCaptionForTarget(post: SocialMediaPostData | null | undefined, target: ChannelEditTarget, caption: string, cleanup = true) {
  if (!post) return;
  if (target === "template" || !shouldUseChannelOverrides(post)) {
    post.caption = caption;
    return;
  }
  const overrideItem = ensureChannelOverride(post, target);
  if (!overrideItem) return;
  overrideItem.caption = caption;
  if (cleanup) cleanupChannelOverrideIfTemplateMatch(post, target);
}

export function getAssetIdsForTarget(post: SocialMediaPostData | null | undefined, target: ChannelEditTarget): Array<string> {
  if (!post) return [];
  if (target === "template") return post.assetIds ?? [];
  if (!shouldUseChannelOverrides(post)) return post.assetIds ?? [];
  const overrideAssets = getChannelOverride(post, target)?.assetIds;
  if ((overrideAssets?.length ?? 0) === 0) return post.assetIds ?? [];
  return overrideAssets ?? [];
}

export function setAssetIdsForTarget(post: SocialMediaPostData | null | undefined, target: ChannelEditTarget, assetIds: Array<string>, cleanup = true) {
  if (!post) return;
  if (target === "template" || !shouldUseChannelOverrides(post)) {
    post.assetIds = assetIds;
    return;
  }
  const overrideItem = ensureChannelOverride(post, target);
  if (!overrideItem) return;
  overrideItem.assetIds = assetIds;
  if (cleanup) cleanupChannelOverrideIfTemplateMatch(post, target);
}

export function isChannelEdited(post: SocialMediaPostData | null | undefined, channel: SocialChannels): boolean {
  if (!shouldUseChannelOverrides(post)) return false;
  const overrideItem = getChannelOverride(post, channel);
  if (!post || !overrideItem) return false;
  const templateCaption = post.caption ?? "";
  const overrideCaption = getCaptionForTarget(post, channel);
  const templateAssets = post.assetIds ?? [];
  const overrideAssets = getAssetIdsForTarget(post, channel);
  return overrideCaption !== templateCaption || !areAssetListsEqual(overrideAssets, templateAssets);
}

export function captionsDifferAcrossChannels(post: SocialMediaPostData | null | undefined, channels: Array<SocialChannels>): boolean {
  if (channels.length < 2) return false;
  const firstCaption = (getCaptionForTarget(post, channels[0]) ?? "").trim();
  for (let i = 1; i < channels.length; i++) {
    if ((getCaptionForTarget(post, channels[i]) ?? "").trim() !== firstCaption) return true;
  }
  return false;
}

export function photosDifferAcrossChannels(post: SocialMediaPostData | null | undefined, channels: Array<SocialChannels>): boolean {
  if (channels.length < 2) return false;
  const firstAssets = getAssetIdsForTarget(post, channels[0]);
  for (let i = 1; i < channels.length; i++) {
    if (!areAssetListsEqual(firstAssets, getAssetIdsForTarget(post, channels[i]))) return true;
  }
  return false;
}

export function getNetworkContentDifferenceSummary(post: SocialMediaPostData | null | undefined, channels: Array<SocialChannels>) {
  const captionDiffers = captionsDifferAcrossChannels(post, channels);
  const photosDiffer = photosDifferAcrossChannels(post, channels);
  return { captionDiffers, photosDiffer, hasAnyDifference: captionDiffers || photosDiffer };
}
